import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { formatIndianCurrency, formatIndianDateTime } from '../../utils/formatters';
import { TrendingUp, CheckCircle, XCircle } from 'lucide-react';

const AdminDeposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [selectedDeposits, setSelectedDeposits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeposits();
  }, [filter]);

  const fetchDeposits = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/deposits', {
        params: filter !== 'all' ? { status: filter } : {}
      });
      // Expect API to return an array of deposit objects
      // We ensure each deposit has a remark field (for controlled textarea)
      const normalized = (res.data || []).map(d => ({ remark: '', ...d }));
      setDeposits(normalized);
    } catch (err) {
      console.error('Failed to fetch deposits:', err);
    } finally {
      setLoading(false);
    }
  };

  // toggle selection for checkboxes (only pending deposits should be selectable)
  const toggleSelect = (depositId) => {
    setSelectedDeposits(prev =>
      prev.includes(depositId) ? prev.filter(id => id !== depositId) : [...prev, depositId]
    );
  };

  // Bulk approve - include remarks if present for each selected deposit
  const handleBulkApprove = async () => {
    if (selectedDeposits.length === 0) {
      alert('Please select deposits first');
      return;
    }

    try {
      // Build payload with ids and remarks map
      const payload = selectedDeposits.map(id => {
        const d = deposits.find(x => x.id === id) || {};
        return { id, remark: d.remark || '' };
      });

      await api.post('/admin/deposits/bulk-approve', { items: payload, approve: true });
      alert('Bulk approval completed');
      setSelectedDeposits([]);
      fetchDeposits();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Bulk approval failed');
    }
  };

  // Approve single deposit (pass remark)
  const handleApprove = async (depositId) => {
    try {
      const deposit = deposits.find(d => d.id === depositId) || {};
      await api.post(`/admin/deposits/${depositId}/approve`, { remark: deposit.remark || '' });
      alert('Deposit approved! Referral reward triggered if applicable.');
      fetchDeposits();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Failed to approve deposit');
    }
  };

  // Reject single deposit (pass remark)
  const handleReject = async (depositId) => {
    try {
      const deposit = deposits.find(d => d.id === depositId) || {};
      await api.post(`/admin/deposits/${depositId}/reject`, { remark: deposit.remark || '' });
      alert('Deposit rejected successfully.');
      fetchDeposits();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Failed to reject deposit');
    }
  };

  // Update local remark for a deposit (controlled textarea)
  const updateRemark = (depositId, value) => {
    setDeposits(prev => prev.map(d => (d.id === depositId ? { ...d, remark: value } : d)));
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gray-900">Deposits Management</h1>
          <p className="text-gray-600 mt-1">Review and approve deposit requests</p>
        </div>

        {/* Bulk Actions */}
        {selectedDeposits.length > 0 && (
          <div className="bg-primary-light border border-primary rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-primary">{selectedDeposits.length} deposits selected</span>
              <button onClick={handleBulkApprove} className="bg-success text-white px-6 py-2 rounded-lg font-semibold">
                Bulk Approve All
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2">
          {['pending', 'active', 'rejected', 'all'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === status ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Deposits List */}
        <div className="space-y-4">
          {loading && (
            <div className="text-center py-8 text-gray-500">Loading deposits...</div>
          )}

          {!loading && deposits.length === 0 && (
            <div className="text-center py-8 text-gray-500">No deposits found</div>
          )}

          {deposits.map((deposit) => (
            <div key={deposit.id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  {deposit.status === 'pending' && (
                    <input
                      type="checkbox"
                      checked={selectedDeposits.includes(deposit.id)}
                      onChange={() => toggleSelect(deposit.id)}
                      className="mt-1 w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{deposit.userName}</h3>
                    <p className="text-sm text-gray-600">{deposit.userEmail}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  deposit.status === 'active' ? 'bg-success-light text-success' :
                  deposit.status === 'pending' ? 'bg-warning-light text-warning' :
                  'bg-error-light text-error'
                }`}>
                  {deposit.status.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Plan</p>
                  <p className="font-semibold text-gray-900">{deposit.planName || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Amount</p>
                  <p className="text-lg font-mono font-bold text-primary">
                    {formatIndianCurrency(deposit.amount)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Transaction ID</p>
                  <p className="font-mono text-xs text-gray-900">{deposit.transactionId}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Date</p>
                  <p className="text-xs text-gray-900">{formatIndianDateTime(deposit.createdAt)}</p>
                </div>
              </div>

              {/* Remark display */}
              {deposit.remark && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-600 mb-1">Remark</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{deposit.remark}</p>
                </div>
              )}

              {/* If pending, show editable remark textarea + approve/reject */}
              {deposit.status === 'pending' && (
                <>
                  <textarea
                    placeholder="Add remark (optional)"
                    className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:ring-2 focus:ring-primary"
                    value={deposit.remark || ''}
                    onChange={(e) => updateRemark(deposit.id, e.target.value)}
                  />

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApprove(deposit.id)}
                      className="flex-1 bg-success hover:bg-success-dark text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Approve Deposit
                    </button>
                    <button
                      onClick={() => handleReject(deposit.id)}
                      className="flex-1 bg-error hover:bg-error-dark text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-5 h-5" />
                      Reject Deposit
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default AdminDeposits;
